import React from 'react';

import type { Brand } from '@flipdish/orgmanagement';
import { ArrowUpRight, Building07 } from '@flipdish/ui-icons';
import { Button } from '@flipdish/ui-library/components/atoms/Button';
import {
  type AvatarCardItemType,
  type OptionPressType,
  AvatarCard,
} from '@flipdish/ui-library/components/molecules/RadioGroup';

type BrandFooterProps = {
  brandId: string;
  label: string;
  /** Lets the shell close a covering panel; the `href` does the navigating. */
  onView: () => void;
};

/** The card's link to the brand's settings page. */
const BrandFooter = ({ brandId, label, onView }: BrandFooterProps) => (
  <Button
    color="link-color"
    size="sm"
    iconTrailing={ArrowUpRight}
    href={`/${brandId}/settings/brand`}
    onPress={onView}
    data-fd={`brand-selector-view-${brandId}`}
  >
    {label}
  </Button>
);

type Props = {
  brands: Brand[];
  /** The brand the user is in, checked in the list. */
  brandId: string | undefined;
  /** Accessible name of the group. */
  label: string;
  viewMoreLabel: string;
  /** Shows the "View more" link to each brand's settings; off for users who may not open that page. */
  showViewMore: boolean;
  /** Switches to the chosen brand, in a new tab when the modifier key was held. */
  onSelectBrand: (brand: Brand, openInNewTab: boolean) => void;
  /** Pressing the brand the user is already in: nothing to switch to, but a covering panel still closes. */
  onSelectCurrentBrand: () => void;
  /** "View more" was pressed; the link's `href` navigates. */
  onViewBrand: () => void;
};

/**
 * The organisation's brands as selectable cards: the checked one is the brand the user is in, and
 * pressing another switches to it in place. A radio fits because the panel stays open, so arrow
 * keys move between brands without leaving it. With `commitOn="press"` the library defers
 * `onChange` to the press: arrow keys only move the check, and a press (click, Space or Enter,
 * with Cmd or Ctrl for a new tab) is what switches, so arrowing past four brands does not
 * navigate four times.
 */
export const BrandCards = ({
  brands,
  brandId,
  label,
  viewMoreLabel,
  showViewMore,
  onSelectBrand,
  onSelectCurrentBrand,
  onViewBrand,
}: Props) => {
  const toItem = (brand: Brand): AvatarCardItemType => ({
    value: brand.brandId ?? '',
    title: brand.name,
    subtitle: brand.brandId,
    src: brand.logoImageUrl || undefined,
    placeholderIcon: Building07,
    // No id means no settings page to link to.
    ...(showViewMore &&
      brand.brandId && {
        footer: <BrandFooter brandId={brand.brandId} label={viewMoreLabel} onView={onViewBrand} />,
      }),
  });

  const handleChange = (value: string, press?: OptionPressType) => {
    const brand = brands.find((candidate) => candidate.brandId === value);
    if (brand) onSelectBrand(brand, Boolean(press?.metaKey || press?.ctrlKey));
  };
  // Pressing the current brand is a change of nothing, but a covering panel still closes.
  const handleOptionPress = (value: string) => {
    if (value === brandId) onSelectCurrentBrand();
  };

  return (
    <AvatarCard
      aria-label={label}
      items={brands.map(toItem)}
      value={brandId ?? null}
      commitOn="press"
      onChange={handleChange}
      onOptionPress={handleOptionPress}
    />
  );
};

export default BrandCards;
